import classNames from 'classnames';
import * as React from 'react';

import Row from '../../components/Row/Row';
import Spacer from '../../components/Spacer/Spacer';
import useTheme from '../../components/ThemeProvider/useTheme';
import Title from '../../components/Title/Title';
import Toaster from '../../components/Toaster/Toaster';
import useIsMobile from '../../hooks/ui/useIsMobile';
import LayoutHeader from '../components/LayoutHeader/LayoutHeader';
import Navigation from '../components/Navigation/Navigation';
import useOverlayStatus from '../components/OverlayStatusContext/useOverlayStatus';
import usePageProps from '../hooks/Inertia/usePageProps';
import Footer from '../pages/BookingForm/components/Footer/Footer';

import LayoutContent from './components/LayoutContent/LayoutContent';
import LogoLink from './components/LogoLink/LogoLink';
import LayoutProps from './types/LayoutProps';

import styles from './DefaultLayout.module.css';

type DefaultLayoutProps = LayoutProps;

const DefaultLayout = ({
    children,
    title,
    width = 'normal',
    inlineLoginUrl,
    inlineRequestPasswordResetUrl,
    headerOptions,
    headerButtons,
}: DefaultLayoutProps) => {
    const theme = useTheme();
    const { tenant, navigationUrls, flash, loggedInCustomer } = usePageProps();
    const isMobile = useIsMobile();
    const { inOverlay } = useOverlayStatus();
    const minimalMode = headerOptions?.minimalOnMobile && isMobile;

    return (
        /**
         * The content id is used by other components to determine the dimensions
         * of the content area. We should probably solve this in a cleaner way.
         */
        <div className={styles.Root} id="content">
            <header className={styles.Header}>
                <div
                    className={classNames(styles.HeaderContent, {
                        [styles.HeaderContent_minimal]: minimalMode,
                    })}
                >
                    {theme.logo ? (
                        <Row horizontal="center">
                            <LogoLink
                                logo={theme.logo}
                                tenant={tenant}
                                url={
                                    loggedInCustomer
                                        ? loggedInCustomer.homePageUrl
                                        : tenant.websiteUrl
                                }
                                minimalMode={minimalMode}
                                target={
                                    inOverlay || loggedInCustomer
                                        ? '_top'
                                        : '_blank'
                                }
                            />
                        </Row>
                    ) : (
                        <Spacer vertical="xlarge" />
                    )}

                    {navigationUrls.length > 0 && (
                        <div
                            className={classNames(styles.NavigationWrapper, {
                                [styles.NavigationWrapper_minimal]: minimalMode,
                            })}
                        >
                            <Navigation
                                items={navigationUrls}
                                loggedInCustomer={loggedInCustomer}
                                preventLoginOrLogout={
                                    headerOptions?.preventLoginOrLogout
                                }
                            />
                        </div>
                    )}
                </div>
            </header>

            <div className={styles.Page}>
                {!minimalMode && title && (
                    <>
                        <Title variant="page" align="center" tag="h1">
                            {title}
                        </Title>
                        <Spacer vertical="large" />
                    </>
                )}

                <div className={classNames(styles.ContentWrapper)}>
                    <LayoutContent width={width} noTopMargin={!!headerButtons}>
                        <LayoutHeader
                            loggedInCustomer={loggedInCustomer}
                            inlineLoginUrl={inlineLoginUrl}
                            inlineRequestPasswordResetUrl={
                                inlineRequestPasswordResetUrl
                            }
                            options={headerOptions}
                        >
                            {headerButtons}
                        </LayoutHeader>

                        {children}
                    </LayoutContent>

                    <Footer />
                </div>
            </div>

            <Toaster>{flash.success}</Toaster>
        </div>
    );
};

export default DefaultLayout;
